import { getAuth, signOut } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import type React from "react";
import type { RefObject } from "react";
import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes } from "react-router-dom";
import { AuthProvider, useAuth } from "./auth/AuthProvider";
import { RequireAuth } from "./auth/RequireAuth";
import { RequireDeveloper } from "./auth/RequireDeveloper";
import { RequirePermission } from "./auth/RequirePermission";
import { RequireSuperAdmin } from "./auth/RequireSuperAdmin";
import { homePathForPermissions } from "./auth/routeAccess";
import { subscribeFeatureFlags, type FeatureFlags, DEFAULT_FEATURE_FLAGS } from "./data/featureFlags";
import { Dashboard } from "./pages/Dashboard";
import { LoginPage } from "./pages/LoginPage";
import { MessagingTruncate } from "./pages/MessagingTruncate";
import { clearWebPushToken, ensureWebPushRegistration } from "./webPush";
import { functions } from "./firebase";

import { ClubMembersPage } from "./pages/clubs/ClubMembersPage";
import { ClubsPage } from "./pages/clubs/ClubsPage";

import { AuditLogsPage } from "./pages/AuditLogsPage";
import { ActivateAccountPage } from "./pages/ActivateAccountPage";
import { BootstrapAdmin } from "./pages/BootstrapAdmin";
import { BetaLandingPage } from "./pages/BetaLandingPage";
import { DataPurge } from "./pages/DataPurge";
import { FamiliesPage } from "./pages/FamiliesPage";
import { InboxToolsPage } from "./pages/InboxToolsPage";
import { MessagingBroadcastPage } from "./pages/MessagingBroadcastPage";
import { MessagingHistoryPage } from "./pages/MessagingHistoryPage";
import { MessagingNewDirectPage } from "./pages/MessagingNewDirectPage";
import { MessagingNewStaffPage } from "./pages/MessagingNewStaffPage";
import { MessagingPage } from "./pages/MessagingPage";
import { OfficeInboxPage } from "./pages/OfficeInboxPage";
import { NewsPage } from "./pages/NewsPage";
import { ParentSignInFallbackPage } from "./pages/ParentSignInFallbackPage";
import { PrivacyPolicyPage } from "./pages/PrivacyPolicyPage";
import { PublicLaunchChecklistPage } from "./pages/PublicLaunchChecklistPage";
import { PublicWebAnalyticsPage } from "./pages/PublicWebAnalyticsPage";
import { PublicWebActivityPage } from "./pages/PublicWebActivityPage";
import { PublicStaffPage } from "./pages/PublicStaffPage";
import { PublicPeoplePage } from "./pages/PublicPeoplePage";
import { PtuConsolePage } from "./pages/PtuConsolePage";
import { SchoolYearPage } from "./pages/SchoolYearPage";
import { SettingsPage } from "./pages/SettingsPage";
import { ThreadDebug } from "./pages/ThreadDebug";
import { UsersPage } from "./pages/UsersPage";
import { VolunteerOperationsPage } from "./pages/VolunteerOperationsPage";
import { VolunteerReviewPage } from "./pages/VolunteerReviewPage";
import { VolunteerSettingsPage } from "./pages/VolunteerSettingsPage";

import { ParentsPage } from "./pages/roster/ParentsPage";
import { RosterHomePage } from "./pages/roster/RosterHomePage";
import { RosterImportPage } from "./pages/roster/RosterImportPage";
import { RosterLinksPage } from "./pages/roster/RosterLinksPage";
import { StudentsPage } from "./pages/roster/StudentsPage";
import { TeachersPage } from "./pages/roster/TeachersPage";

const CalendarPage = lazy(() => import("./pages/CalendarPage").then((m) => ({ default: m.CalendarPage })));
const AccessRolesPage = lazy(() => import("./pages/AccessRolesPage").then((m) => ({ default: m.AccessRolesPage })));
const CustomFormsPage = lazy(() => import("./pages/CustomFormsPage").then((m) => ({ default: m.CustomFormsPage })));
const DeveloperPortalPage = lazy(() => import("./pages/DeveloperPortalPage").then((m) => ({ default: m.DeveloperPortalPage })));
const FamilyCrmPage = lazy(() => import("./pages/FamilyCrmPage").then((m) => ({ default: m.FamilyCrmPage })));
const HelpCenterPage = lazy(() => import("./pages/HelpCenterPage").then((m) => ({ default: m.HelpCenterPage })));
const PublicDonationsPage = lazy(() => import("./pages/PublicDonationsPage").then((m) => ({ default: m.PublicDonationsPage })));
const PublicInquiriesPage = lazy(() => import("./pages/PublicInquiriesPage").then((m) => ({ default: m.PublicInquiriesPage })));
const PublicNewslettersPage = lazy(() => import("./pages/PublicNewslettersPage").then((m) => ({ default: m.PublicNewslettersPage })));
const PublicPagesCmsPage = lazy(() => import("./pages/PublicPagesCmsPage").then((m) => ({ default: m.PublicPagesCmsPage })));
const PublicThemePage = lazy(() => import("./pages/PublicThemePage").then((m) => ({ default: m.PublicThemePage })));
const TechInventoryPage = lazy(() => import("./pages/TechInventoryPage").then((m) => ({ default: m.TechInventoryPage })));

type AdminTheme = "light" | "dark";

function readStoredTheme(): AdminTheme {
  try {
    return window.localStorage.getItem("zca-admin-theme") === "dark" ? "dark" : "light";
  } catch {
    return "light";
  }
}

function NotFound() {
  return (
    <div className="center">
      <div className="card">
        <h2>Not found</h2>
        <p className="muted">That page doesn’t exist.</p>
        <Link to="/" className="btn secondary">
          Go home
        </Link>
      </div>
    </div>
  );
}

function HomeRoute() {
  const { user, hasPermission } = useAuth();

  const canDashboard = hasPermission("dashboard", "view");
  const canNews = hasPermission("news", "view");
  const canPublicWeb = hasPermission("publicWebPages", "view");
  const canDeveloper = hasPermission("developerPortal", "view");
  const homePath = homePathForPermissions(hasPermission);

  if (!canDashboard && (canPublicWeb || canDeveloper || canNews)) {
    return <Navigate to={homePath} replace />;
  }

  if (!canDashboard) {
    return (
      <div className="center">
        <div className="card" style={{ maxWidth: 560 }}>
          <h2>Access Not Configured</h2>
          <p className="muted">
            You are signed in as <b>{user?.email || "this account"}</b>, but this account does not have an admin-web role yet.
          </p>
          <p className="muted">Ask a super admin to assign the correct role, then sign out and sign back in.</p>
        </div>
      </div>
    );
  }

  return <Dashboard />;
}

function BrowserPushManager() {
  const { user, hasPermission } = useAuth();
  const canUsePush = hasPermission("messaging", "view") || hasPermission("news", "view");

  useEffect(() => {
    let cleanup: (() => void) | undefined;

    (async () => {
      try {
        if (user && canUsePush) {
          cleanup = await ensureWebPushRegistration(user);
        } else {
          await clearWebPushToken();
        }
      } catch (e) {
        console.warn("[web-push] Browser push registration failed.", e);
      }
    })();

    return () => {
      if (cleanup) cleanup();
    };
  }, [user?.uid, canUsePush]);

  return null;
}

type InboxThreadLike = {
  id?: string;
  hasUnread?: boolean;
  lastMessageAt?: any;
  lastMessageSenderId?: string;
  lastReadAt?: any;
};

function toMillisSafe(v: any): number {
  if (!v) return 0;
  try {
    if (typeof v?.toMillis === "function") return v.toMillis();
    if (typeof v?.toDate === "function") return v.toDate().getTime();
    const seconds = v.seconds ?? v._seconds;
    const nanos = v.nanoseconds ?? v._nanoseconds;
    if (typeof seconds === "number") {
      return seconds * 1000 + (typeof nanos === "number" ? Math.floor(nanos / 1e6) : 0);
    }
    const d = new Date(v);
    const t = d.getTime();
    return Number.isFinite(t) ? t : 0;
  } catch {
    return 0;
  }
}

function isUnreadThread(thread: InboxThreadLike, uid: string, localReadAtByThread: Record<string, number>): boolean {
  const localReadAt = localReadAtByThread[String(thread.id || "")] || 0;
  const lastMessageMs = toMillisSafe(thread.lastMessageAt);
  if (localReadAt && lastMessageMs <= localReadAt) return false;
  if (thread.hasUnread === true) return true;
  if (thread.hasUnread === false) return false;

  const lastRead = thread.lastReadAt?.[uid] ?? thread.lastReadAt;
  return (
    String(thread.lastMessageSenderId || "") !== uid &&
    lastMessageMs > toMillisSafe(lastRead)
  );
}

function FeatureDisabled({ title }: { title: string }) {
  return (
    <div className="center">
      <div className="card" style={{ maxWidth: 560 }}>
        <h2>{title}</h2>
        <p className="muted">This module is currently disabled by an admin setting.</p>
        <Link to="/" className="btn secondary">
          Go home
        </Link>
      </div>
    </div>
  );
}

function RequirePtuPermission({ children }: { children: React.ReactNode }) {
  const { loading, hasPermission } = useAuth();
  const homePath = homePathForPermissions(hasPermission);
  const canAccessPtu =
    hasPermission("ptuSettings", "view") ||
    hasPermission("ptuMessaging", "view") ||
    hasPermission("ptuSignups", "view");

  if (loading) {
    return (
      <div className="center">
        <div className="card">
          <h3>Loading...</h3>
          <p className="muted">Checking permissions.</p>
        </div>
      </div>
    );
  }

  if (!canAccessPtu) {
    return (
      <div className="center">
        <div className="card">
          <h3>Access denied</h3>
          <p className="muted">Your account is signed in, but does not have access for PTU tools.</p>
          <Link to={homePath} className="btn secondary">Go home</Link>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}

function useUnreadMessageCount(userUid?: string | null, enabled?: boolean) {
  const [count, setCount] = useState(0);
  const [localReadAtByThread, setLocalReadAtByThread] = useState<Record<string, number>>({});
  const [activeThreadId, setActiveThreadId] = useState<string | null>(null);

  useEffect(() => {
    function onThreadRead(event: Event) {
      const detail = (event as CustomEvent<{ threadId?: string; readAtMillis?: number }>).detail || {};
      const threadId = String(detail.threadId || "").trim();
      if (!threadId) return;

      const readAtMillis = Number(detail.readAtMillis);
      const nextReadAt = Number.isFinite(readAtMillis) && readAtMillis > 0 ? readAtMillis : Date.now();
      setLocalReadAtByThread((prev) => ({
        ...prev,
        [threadId]: Math.max(prev[threadId] || 0, nextReadAt),
      }));
    }

    function onActiveThread(event: Event) {
      const detail = (event as CustomEvent<{ threadId?: string | null }>).detail || {};
      const threadId = String(detail.threadId || "").trim();
      setActiveThreadId(threadId || null);
    }

    window.addEventListener("zca:thread-read", onThreadRead);
    window.addEventListener("zca:active-thread", onActiveThread);
    return () => {
      window.removeEventListener("zca:thread-read", onThreadRead);
      window.removeEventListener("zca:active-thread", onActiveThread);
    };
  }, []);

  useEffect(() => {
    if (!userUid || !enabled) {
      setCount(0);
      return;
    }

    let cancelled = false;
    const uid = userUid;

    async function refresh() {
      try {
        const fn = httpsCallable(functions, "getInboxThreads");
        const res: any = await fn({ limit: 250 });
        if (cancelled) return;

        const threads = Array.isArray(res?.data?.threads) ? res.data.threads : [];
        setCount(
          threads.filter((t: InboxThreadLike) => {
            if (activeThreadId && String(t.id || "") === activeThreadId) return false;
            return isUnreadThread(t, uid, localReadAtByThread);
          }).length
        );
      } catch (e) {
        console.warn("[messaging] Failed to load unread count.", e);
      }
    }

    refresh();
    const timer = window.setInterval(refresh, 15000);

    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener("focus", onFocus);
    };
  }, [userUid, enabled, localReadAtByThread, activeThreadId]);

  return count;
}

function AppShell() {
  const { user, hasPermission } = useAuth();
  const [theme, setTheme] = useState<AdminTheme>(readStoredTheme);
  const [loggingOut, setLoggingOut] = useState(false);
  const [featureFlags, setFeatureFlags] = useState<FeatureFlags>(DEFAULT_FEATURE_FLAGS);
  const communicationsMenuRef = useRef<HTMLDetailsElement | null>(null);
  const crmMenuRef = useRef<HTMLDetailsElement | null>(null);
  const operationsMenuRef = useRef<HTMLDetailsElement | null>(null);
  const publicWebMenuRef = useRef<HTMLDetailsElement | null>(null);
  const adminMenuRef = useRef<HTMLDetailsElement | null>(null);
  const developerMenuRef = useRef<HTMLDetailsElement | null>(null);
  const advancedToolsMenuRef = useRef<HTMLDetailsElement | null>(null);
  const canDashboard = hasPermission("dashboard", "view");
  const canCalendar = hasPermission("calendar", "view");
  const canNews = hasPermission("news", "view");
  const canMessaging = hasPermission("messaging", "view");
  const canEditMessaging = hasPermission("messaging", "edit");
  const canMessageHistory = hasPermission("messageHistory", "view");
  const canFamilyCrm = featureFlags.familyCrmEnabled && hasPermission("familyCrm", "view");
  const canAdmissionsCrm = hasPermission("admissionsCrm", "view");
  const canDonorCrm = hasPermission("donorCrm", "view");
  const canRoster = hasPermission("roster", "view");
  const canFamilies = hasPermission("families", "view");
  const canSchoolYear = hasPermission("schoolYear", "view");
  const canVolunteerReview = hasPermission("volunteerReview", "view");
  const canVolunteerOps = hasPermission("volunteerOps", "view");
  const canVolunteerSettings = hasPermission("volunteerSettings", "view");
  const canClubs = hasPermission("clubs", "view");
  const canPtu = hasPermission("ptuSettings", "view") || hasPermission("ptuMessaging", "view") || hasPermission("ptuSignups", "view");
  const canTechInventory = hasPermission("techInventory", "view");
  const canPublicWebPages = hasPermission("publicWebPages", "view");
  const canPublicWebTheme = hasPermission("publicWebTheme", "view");
  const canPublicWebForms = hasPermission("publicWebForms", "view");
  const canPublicWebPeople = hasPermission("publicWebPeople", "view");
  const canPublicWebActivity = hasPermission("publicWebActivity", "view");
  const canPublicWebAnalytics = hasPermission("publicWebAnalytics", "view");
  const canUsers = hasPermission("users", "view");
  const canSettings = hasPermission("settings", "view");
  const canAuditLogs = hasPermission("auditLogs", "view");
  const canAccessControl = hasPermission("accessControl", "view");
  const canDeveloperPortal = hasPermission("developerPortal", "view");
  const canAdvancedTools = hasPermission("advancedTools", "view");
  const canDataPurge = hasPermission("dataPurge", "manage");
  const canUsePush = canMessaging || canNews;
  const unreadMessageCount = useUnreadMessageCount(user?.uid, canUsePush);
  const hasCommunicationMenu = canCalendar || canNews || canMessaging || canMessageHistory;
  const hasCrmMenu = canFamilyCrm || canAdmissionsCrm || canDonorCrm;
  const hasOperationsMenu = canRoster || canFamilies || canSchoolYear || canVolunteerReview || canVolunteerOps || canVolunteerSettings || canClubs || canPtu || canTechInventory;
  const hasPublicWebMenu = canPublicWebPages || canPublicWebTheme || canPublicWebForms || canPublicWebPeople || canPublicWebActivity;
  const hasAdminMenu = canUsers || canSettings || canPublicWebAnalytics || canAuditLogs || canAccessControl;

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    try {
      window.localStorage.setItem("zca-admin-theme", theme);
    } catch {
      // ignore storage failures
    }
  }, [theme]);

  useEffect(() => {
    if (!user) {
      setFeatureFlags(DEFAULT_FEATURE_FLAGS);
      return;
    }
    return subscribeFeatureFlags(setFeatureFlags);
  }, [user?.uid]);

  async function handleLogout() {
    if (loggingOut) return;
    setLoggingOut(true);

    try {
      await clearWebPushToken();
    } catch (e) {
      console.warn("[auth] Failed clearing web push token during logout.", e);
    }

    try {
      await signOut(getAuth());
    } catch (e) {
      console.error("[auth] Logout failed.", e);
      alert("Logout failed. Please try again.");
    } finally {
      setLoggingOut(false);
    }
  }

  function closeMenu(ref: RefObject<HTMLDetailsElement | null>) {
    if (ref.current) ref.current.open = false;
  }

  function closeOtherMenus(activeRef: RefObject<HTMLDetailsElement | null>) {
    [communicationsMenuRef, crmMenuRef, operationsMenuRef, publicWebMenuRef, adminMenuRef, developerMenuRef, advancedToolsMenuRef]
      .filter((ref) => ref !== activeRef)
      .forEach(closeMenu);
  }

  function closeAllMenus() {
    [communicationsMenuRef, crmMenuRef, operationsMenuRef, publicWebMenuRef, adminMenuRef, developerMenuRef, advancedToolsMenuRef].forEach(closeMenu);
  }

  return (
    <>
      <BrowserPushManager />

      <div className="topbar">
        <div className="topbar-inner">
          <Link to={canDashboard ? "/" : canNews ? "/news" : "/"} className="brand brand-link">
            <img src="/logo.png" alt="Zion Classical Academy" className="brand-logo" />
            <div>
              <div className="brand-title">Zion Classical Academy</div>
              <div className="brand-subtitle">Admin Web</div>
            </div>
          </Link>

          {user ? (
            <nav className="nav nav-shell">
              {canDashboard ? <Link to="/" className="navlink">Dashboard</Link> : null}
              {hasCommunicationMenu ? (
                <details className="navmenu" ref={communicationsMenuRef} onToggle={(e) => e.currentTarget.open ? closeOtherMenus(communicationsMenuRef) : undefined}>
                  <summary className="navlink navmenu-trigger">
                    Communications{unreadMessageCount > 0 ? <span className="nav-badge">{unreadMessageCount > 99 ? "99+" : unreadMessageCount}</span> : null}
                  </summary>
                  <div className="navmenu-panel">
                    {canCalendar ? <Link to="/calendar" className="navmenu-item" onClick={closeAllMenus}>Calendar</Link> : null}
                    {canNews ? <Link to="/news" className="navmenu-item" onClick={closeAllMenus}>News</Link> : null}
                    {canMessaging ? <Link to="/messaging" className="navmenu-item" onClick={closeAllMenus}>Messaging</Link> : null}
                    {canMessaging ? <Link to="/messaging/office" className="navmenu-item" onClick={closeAllMenus}>School Office Inbox</Link> : null}
                    {canEditMessaging ? <Link to="/messaging/new-staff" className="navmenu-item" onClick={closeAllMenus}>New Staff Message</Link> : null}
                    {canEditMessaging ? <Link to="/messaging/new-announcement" className="navmenu-item" onClick={closeAllMenus}>New Announcement</Link> : null}
                    {canMessageHistory ? <Link to="/messaging/history" className="navmenu-item" onClick={closeAllMenus}>Message History</Link> : null}
                  </div>
                </details>
              ) : null}

              {hasCrmMenu ? (
                <details className="navmenu" ref={crmMenuRef} onToggle={(e) => e.currentTarget.open ? closeOtherMenus(crmMenuRef) : undefined}>
                  <summary className="navlink navmenu-trigger">CRM</summary>
                  <div className="navmenu-panel">
                    {canFamilyCrm ? <Link to="/family-crm" className="navmenu-item" onClick={closeAllMenus}>Family CRM</Link> : null}
                    {canAdmissionsCrm ? <Link to="/public-web/inquiries" className="navmenu-item" onClick={closeAllMenus}>Admissions CRM</Link> : null}
                    {canDonorCrm ? <Link to="/public-web/donations" className="navmenu-item" onClick={closeAllMenus}>Donors & Campaigns</Link> : null}
                  </div>
                </details>
              ) : null}

              {hasOperationsMenu ? (
                <details className="navmenu" ref={operationsMenuRef} onToggle={(e) => e.currentTarget.open ? closeOtherMenus(operationsMenuRef) : undefined}>
                  <summary className="navlink navmenu-trigger">Operations</summary>
                  <div className="navmenu-panel">
                    {canRoster ? <Link to="/roster" className="navmenu-item" onClick={closeAllMenus}>Roster</Link> : null}
                    {canFamilies ? <Link to="/families" className="navmenu-item" onClick={closeAllMenus}>Families</Link> : null}
                    {canSchoolYear ? <Link to="/school-year" className="navmenu-item" onClick={closeAllMenus}>School Year</Link> : null}
                    {canVolunteerReview ? <Link to="/volunteer/review" className="navmenu-item" onClick={closeAllMenus}>Volunteer Review</Link> : null}
                    {canVolunteerOps ? <Link to="/volunteer/operations" className="navmenu-item" onClick={closeAllMenus}>Volunteer Ops</Link> : null}
                    {canVolunteerSettings ? <Link to="/volunteer/settings" className="navmenu-item" onClick={closeAllMenus}>Volunteer Settings</Link> : null}
                    {canClubs ? <Link to="/clubs" className="navmenu-item" onClick={closeAllMenus}>Clubs</Link> : null}
                    {canPtu ? <Link to="/ptu" className="navmenu-item" onClick={closeAllMenus}>PTU</Link> : null}
                    {canTechInventory ? <Link to="/technology" className="navmenu-item" onClick={closeAllMenus}>Technology Inventory</Link> : null}
                  </div>
                </details>
              ) : null}

              {hasPublicWebMenu ? (
                <details className="navmenu" ref={publicWebMenuRef} onToggle={(e) => e.currentTarget.open ? closeOtherMenus(publicWebMenuRef) : undefined}>
                  <summary className="navlink navmenu-trigger">Public Web</summary>
                  <div className="navmenu-panel">
                    {canPublicWebPages ? <Link to="/public-web/pages" className="navmenu-item" onClick={closeAllMenus}>Pages</Link> : null}
                    {canPublicWebTheme ? <Link to="/public-web/theme" className="navmenu-item" onClick={closeAllMenus}>Theme</Link> : null}
                    {canPublicWebForms ? <Link to="/public-web/forms" className="navmenu-item" onClick={closeAllMenus}>Forms</Link> : null}
                    {canPublicWebPages ? <Link to="/public-web/newsletters" className="navmenu-item" onClick={closeAllMenus}>Newsletters</Link> : null}
                    {canPublicWebPeople ? <Link to="/public-web/staff" className="navmenu-item" onClick={closeAllMenus}>Public Staff</Link> : null}
                    {canPublicWebPeople ? <Link to="/public-web/board" className="navmenu-item" onClick={closeAllMenus}>Board</Link> : null}
                    {canPublicWebPeople ? <Link to="/public-web/ptu" className="navmenu-item" onClick={closeAllMenus}>PTU Leadership</Link> : null}
                    {canPublicWebActivity ? <Link to="/public-web/activity" className="navmenu-item" onClick={closeAllMenus}>Activity</Link> : null}
                    {canPublicWebActivity ? <Link to="/public-web/launch-checklist" className="navmenu-item" onClick={closeAllMenus}>Launch Checklist</Link> : null}
                  </div>
                </details>
              ) : null}

              {hasAdminMenu ? (
                <details className="navmenu" ref={adminMenuRef} onToggle={(e) => e.currentTarget.open ? closeOtherMenus(adminMenuRef) : undefined}>
                  <summary className="navlink navmenu-trigger">Admin</summary>
                  <div className="navmenu-panel">
                    {canUsers ? <Link to="/users" className="navmenu-item" onClick={closeAllMenus}>Users</Link> : null}
                    {canAccessControl ? <Link to="/access-roles" className="navmenu-item" onClick={closeAllMenus}>Roles & Permissions</Link> : null}
                    {canSettings ? <Link to="/settings" className="navmenu-item" onClick={closeAllMenus}>Settings</Link> : null}
                    {canPublicWebAnalytics ? <Link to="/public-web/analytics" className="navmenu-item" onClick={closeAllMenus}>Web Analytics</Link> : null}
                    {canAuditLogs ? <Link to="/audit-logs" className="navmenu-item" onClick={closeAllMenus}>Audit Logs</Link> : null}
                  </div>
                </details>
              ) : null}

              {canDeveloperPortal ? (
                <details className="navmenu" ref={developerMenuRef} onToggle={(e) => e.currentTarget.open ? closeOtherMenus(developerMenuRef) : undefined}>
                  <summary className="navlink navmenu-trigger">Developer</summary>
                  <div className="navmenu-panel">
                    <Link to="/developer" className="navmenu-item" onClick={closeAllMenus}>Developer Portal</Link>
                  </div>
                </details>
              ) : null}

              {canAdvancedTools ? (
                <details className="navmenu" ref={advancedToolsMenuRef} onToggle={(e) => e.currentTarget.open ? closeOtherMenus(advancedToolsMenuRef) : undefined}>
                  <summary className="navlink navmenu-trigger">Advanced Tools</summary>
                  <div className="navmenu-panel">
                    <Link to="/messaging/inbox-tools" className="navmenu-item" onClick={closeAllMenus}>Inbox Tools</Link>
                    <Link to="/messaging/thread-debug" className="navmenu-item" onClick={closeAllMenus}>Thread Access Diagnostics</Link>
                    <Link to="/messaging/truncate" className="navmenu-item" onClick={closeAllMenus}>Thread Maintenance</Link>
                    {canDataPurge ? <Link to="/data/purge" className="navmenu-item navmenu-item-danger" onClick={closeAllMenus}>Data Purge</Link> : null}
                  </div>
                </details>
              ) : null}

              <Link to="/help" className="navlink" onClick={closeAllMenus}>Help</Link>

              <button
                type="button"
                className="theme-toggle"
                onClick={() => setTheme((current) => current === "dark" ? "light" : "dark")}
                aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
                title={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
              >
                <span aria-hidden="true">{theme === "dark" ? "Light" : "Dark"}</span>
              </button>

              <button
                type="button"
                className="btn secondary"
                onClick={handleLogout}
                disabled={loggingOut}
                style={{ marginLeft: 8 }}
              >
                {loggingOut ? "Logging out…" : "Logout"}
              </button>
            </nav>
          ) : null}
        </div>
      </div>

      <div className="container">
        <Suspense fallback={
          <div className="center">
            <div className="card">
              <h3>Loading page...</h3>
              <p className="muted">Preparing the admin tools.</p>
            </div>
          </div>
        }>
        <Routes>
          <Route path="/activate" element={<ActivateAccountPage />} />
          <Route path="/auth/parent-sign-in" element={<ParentSignInFallbackPage />} />
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/beta" element={<BetaLandingPage />} />
          <Route path="/app-beta" element={<Navigate to="/beta" replace />} />
          <Route path="/dashboard" element={<RequireAuth><Navigate to="/" replace /></RequireAuth>} />

          <Route path="/calendar" element={<RequireAuth><RequirePermission moduleId="calendar"><CalendarPage /></RequirePermission></RequireAuth>} />
          <Route path="/news" element={<RequireAuth><RequirePermission moduleId="news"><NewsPage /></RequirePermission></RequireAuth>} />
          <Route path="/messaging" element={<RequireAuth><RequirePermission moduleId="messaging"><MessagingPage /></RequirePermission></RequireAuth>} />
          <Route path="/messaging/office" element={<RequireAuth><RequirePermission moduleId="messaging"><OfficeInboxPage /></RequirePermission></RequireAuth>} />
          <Route path="/messaging/new-direct" element={<RequireAuth><RequirePermission moduleId="messaging" level="edit"><MessagingNewDirectPage /></RequirePermission></RequireAuth>} />
          <Route path="/messaging/new-staff" element={<RequireAuth><RequirePermission moduleId="messaging" level="edit"><MessagingNewStaffPage /></RequirePermission></RequireAuth>} />
          <Route path="/messaging/new-announcement" element={<RequireAuth><RequirePermission moduleId="messaging" level="edit"><MessagingBroadcastPage /></RequirePermission></RequireAuth>} />
          <Route path="/messaging/history" element={<RequireAuth><RequirePermission moduleId="messageHistory"><MessagingHistoryPage /></RequirePermission></RequireAuth>} />
          <Route path="/audit-logs" element={<RequireAuth><RequirePermission moduleId="auditLogs"><AuditLogsPage /></RequirePermission></RequireAuth>} />
          <Route path="/settings" element={<RequireAuth><RequirePermission moduleId="settings"><SettingsPage /></RequirePermission></RequireAuth>} />
          <Route path="/school-year" element={<RequireAuth><RequirePermission moduleId="schoolYear"><SchoolYearPage /></RequirePermission></RequireAuth>} />
          <Route path="/developer" element={<RequireAuth><RequireDeveloper><DeveloperPortalPage /></RequireDeveloper></RequireAuth>} />
          <Route path="/volunteer/review" element={<RequireAuth><RequirePermission moduleId="volunteerReview"><VolunteerReviewPage /></RequirePermission></RequireAuth>} />
          <Route path="/volunteer/operations" element={<RequireAuth><RequirePermission moduleId="volunteerOps"><VolunteerOperationsPage /></RequirePermission></RequireAuth>} />
          <Route path="/volunteer/settings" element={<RequireAuth><RequirePermission moduleId="volunteerSettings"><VolunteerSettingsPage /></RequirePermission></RequireAuth>} />
          <Route path="/technology" element={<RequireAuth><RequirePermission moduleId="techInventory"><TechInventoryPage /></RequirePermission></RequireAuth>} />
          <Route path="/users" element={<RequireAuth><RequirePermission moduleId="users"><UsersPage /></RequirePermission></RequireAuth>} />
          <Route path="/access-roles" element={<RequireAuth><RequirePermission moduleId="accessControl"><AccessRolesPage /></RequirePermission></RequireAuth>} />
          <Route path="/bootstrap" element={<RequireAuth><RequireSuperAdmin><BootstrapAdmin /></RequireSuperAdmin></RequireAuth>} />
          <Route path="/clubs" element={<RequireAuth><RequirePermission moduleId="clubs"><ClubsPage /></RequirePermission></RequireAuth>} />
          <Route path="/clubs/:clubId" element={<RequireAuth><RequirePermission moduleId="clubs"><ClubMembersPage /></RequirePermission></RequireAuth>} />
          <Route path="/clubs/:clubId/members" element={<RequireAuth><RequirePermission moduleId="clubs"><ClubMembersPage /></RequirePermission></RequireAuth>} />
          <Route path="/ptu" element={<RequireAuth><RequirePtuPermission><PtuConsolePage /></RequirePtuPermission></RequireAuth>} />
          <Route path="/roster" element={<RequireAuth><RequirePermission moduleId="roster"><RosterHomePage /></RequirePermission></RequireAuth>} />
          <Route path="/roster/students" element={<RequireAuth><RequirePermission moduleId="roster"><StudentsPage /></RequirePermission></RequireAuth>} />
          <Route path="/roster/parents" element={<RequireAuth><RequirePermission moduleId="roster"><ParentsPage /></RequirePermission></RequireAuth>} />
          <Route path="/roster/teachers" element={<RequireAuth><RequirePermission moduleId="roster"><TeachersPage /></RequirePermission></RequireAuth>} />
          <Route path="/roster/links" element={<RequireAuth><RequirePermission moduleId="roster"><RosterLinksPage /></RequirePermission></RequireAuth>} />
          <Route path="/roster/import" element={<RequireAuth><RequirePermission moduleId="roster" level="manage"><RosterImportPage /></RequirePermission></RequireAuth>} />
          <Route path="/families" element={<RequireAuth><RequirePermission moduleId="families"><FamiliesPage /></RequirePermission></RequireAuth>} />
          <Route path="/family-crm" element={<RequireAuth><RequirePermission moduleId="familyCrm">{featureFlags.familyCrmEnabled ? <FamilyCrmPage /> : <FeatureDisabled title="Family CRM Disabled" />}</RequirePermission></RequireAuth>} />
          <Route path="/public-web" element={<RequireAuth><RequirePermission moduleId="publicWebPages"><Navigate to="/public-web/pages" replace /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/pages" element={<RequireAuth><RequirePermission moduleId="publicWebPages"><PublicPagesCmsPage /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/theme" element={<RequireAuth><RequirePermission moduleId="publicWebTheme"><PublicThemePage /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/forms" element={<RequireAuth><RequirePermission moduleId="publicWebForms"><CustomFormsPage /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/announcements" element={<RequireAuth><RequirePermission moduleId="publicWebPages"><Navigate to="/public-web/newsletters" replace /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/newsletters" element={<RequireAuth><RequirePermission moduleId="publicWebPages"><PublicNewslettersPage /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/donations" element={<RequireAuth><RequirePermission moduleId="donorCrm"><PublicDonationsPage /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/inquiries" element={<RequireAuth><RequirePermission moduleId="admissionsCrm"><PublicInquiriesPage /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/activity" element={<RequireAuth><RequirePermission moduleId="publicWebActivity"><PublicWebActivityPage /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/launch-checklist" element={<RequireAuth><RequirePermission moduleId="publicWebActivity"><PublicLaunchChecklistPage /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/analytics" element={<RequireAuth><RequirePermission moduleId="publicWebAnalytics"><PublicWebAnalyticsPage /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/staff" element={<RequireAuth><RequirePermission moduleId="publicWebPeople"><PublicStaffPage /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/board" element={<RequireAuth><RequirePermission moduleId="publicWebPeople"><PublicPeoplePage collectionName="publicBoard" title="Public Website Board" description="Manage Board of Directors entries shown on the public website." /></RequirePermission></RequireAuth>} />
          <Route path="/public-web/ptu" element={<RequireAuth><RequirePermission moduleId="publicWebPeople"><PublicPeoplePage collectionName="publicPtuLeadership" title="PTU Leadership" description="Manage Parent Teachers United leadership entries shown on the public website. Leave names blank for open or TBD roles." /></RequirePermission></RequireAuth>} />
          <Route path="/public-staff" element={<RequireAuth><RequirePermission moduleId="publicWebPeople"><Navigate to="/public-web/staff" replace /></RequirePermission></RequireAuth>} />
          <Route path="/messaging/truncate" element={<RequireAuth><RequireSuperAdmin><MessagingTruncate /></RequireSuperAdmin></RequireAuth>} />
          <Route path="/messaging/inbox-tools" element={<RequireAuth><RequireSuperAdmin><InboxToolsPage /></RequireSuperAdmin></RequireAuth>} />
          <Route path="/messaging/thread-debug" element={<RequireAuth><RequireSuperAdmin><ThreadDebug /></RequireSuperAdmin></RequireAuth>} />
          <Route path="/data/purge" element={<RequireAuth><RequirePermission moduleId="dataPurge" level="manage"><DataPurge /></RequirePermission></RequireAuth>} />
          <Route path="/help" element={<RequireAuth><HelpCenterPage /></RequireAuth>} />
          <Route path="/" element={<RequireAuth><HomeRoute /></RequireAuth>} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </div>
    </>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppShell />
      </AuthProvider>
    </BrowserRouter>
  );
}
